import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Input,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { TwDatePicker } from 'components/DatePicker';
import { Currency } from 'components/Number';
import { CurrencyNumberInput } from 'components/NumberInput';
import SelectInput from 'components/inputs/SelectInput';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import {
  createOtherChargeLine,
  getAdditionalObligationOptions,
  isOtherChargeLine,
  isOtherChargeComplete,
  sumIncludedLineAmounts,
} from '../calc/suggestObligations';

const ObligationLinesTable = ({
  lines,
  onChange,
  remainingInstallments,
  facilityFees,
  commitmentLength,
}) => {
  const [selectedAdditionalLine, setSelectedAdditionalLine] = useState(null);

  const includedLines = useMemo(
    () => lines.filter((line) => line.included),
    [lines]
  );

  const additionalOptions = useMemo(
    () =>
      getAdditionalObligationOptions({
        remainingInstallments,
        facilityFees,
        existingLineIds: includedLines.map((line) => line.id),
        commitmentLength,
      }),
    [commitmentLength, facilityFees, includedLines, remainingInstallments]
  );

  const toggleLine = (lineId, included) => {
    onChange(lines.map((line) => (line.id === lineId ? { ...line, included } : line)));
  };

  const updateLine = (lineId, patch) => {
    onChange(lines.map((line) => (line.id === lineId ? { ...line, ...patch } : line)));
  };

  // A charge line exists only on this unsaved invoice, so removing it drops it
  // outright rather than unchecking it the way an obligation line is handled.
  const removeLine = (lineId) => {
    onChange(lines.filter((line) => line.id !== lineId));
  };

  const addChargeLine = () => {
    onChange([...lines, createOtherChargeLine()]);
  };

  const addLine = () => {
    const option = additionalOptions.find((item) => item.value === selectedAdditionalLine);
    if (!option) {
      return;
    }
    const existingLine = lines.find((line) => line.id === option.line.id);
    if (existingLine) {
      onChange([
        ...lines.filter((line) => line.id !== option.line.id),
        { ...existingLine, included: true },
      ]);
    } else {
      onChange([...lines, option.line]);
    }
    setSelectedAdditionalLine(null);
  };

  const totalDue = sumIncludedLineAmounts(lines);
  const incompleteCharges = includedLines.filter(
    (line) => isOtherChargeLine(line) && !isOtherChargeComplete(line)
  );

  return (
    <Box>
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th w="48px">Include</Th>
            <Th>Description</Th>
            <Th>Due date</Th>
            <Th isNumeric>Amount</Th>
          </Tr>
        </Thead>
        <Tbody>
          {includedLines.map((line) =>
            isOtherChargeLine(line) ? (
              <Tr key={line.id}>
                <Td>
                  <Checkbox
                    isChecked={line.included}
                    onChange={(event) => toggleLine(line.id, event.target.checked)}
                  />
                </Td>
                <Td>
                  <Input
                    size="sm"
                    placeholder="Description (e.g. Lawyers fee)"
                    value={line.description || ''}
                    isInvalid={!String(line.description || '').trim()}
                    onChange={(event) => updateLine(line.id, { description: event.target.value })}
                  />
                </Td>
                <Td>
                  {/* An obligation carries the due date of the installment or
                      fee behind it. A charge has nothing behind it, so the user
                      sets the date; it then feeds the invoice due date through
                      computeDueDateFromLines exactly as an obligation's does. */}
                  <Box minW="150px">
                    <TwDatePicker
                      value={line.dueDate}
                      onChange={(value) => updateLine(line.id, { dueDate: value })}
                    />
                  </Box>
                </Td>
                <Td isNumeric>
                  <Flex gap={2} align="center" justify="flex-end">
                    <Box minW="120px">
                      <CurrencyNumberInput
                        value={line.amount}
                        isInvalid={!(Number(line.amount) > 0)}
                        onChange={(value) => updateLine(line.id, { amount: value })}
                        showPrefix
                      />
                    </Box>
                    <IconButton
                      aria-label="Remove charge"
                      icon={<CloseIcon />}
                      size="xs"
                      variant="ghost"
                      onClick={() => removeLine(line.id)}
                    />
                  </Flex>
                </Td>
              </Tr>
            ) : (
              <Tr key={line.id}>
                <Td>
                  <Checkbox
                    isChecked={line.included}
                    onChange={(event) => toggleLine(line.id, event.target.checked)}
                  />
                </Td>
                <Td>{line.description}</Td>
                <Td>{line.dueDate ? moment(line.dueDate).format('MM/DD/YYYY') : '—'}</Td>
                <Td isNumeric>
                  <Currency value={line.amount} maximumFractionDigits={2} />
                </Td>
              </Tr>
            )
          )}
          {!includedLines.length && (
            <Tr>
              <Td colSpan={4}>
                <Text color="secondaryGray.600">
                  No payment obligations selected. Add a line below.
                </Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>

      <Flex mt={4} gap={3} align="center">
        <Box minW="320px">
          <SelectInput
            placeholder="Add another line"
            options={additionalOptions}
            value={selectedAdditionalLine}
            onChange={(value) => setSelectedAdditionalLine(value ?? null)}
            enableSearch
            isClearable
          />
        </Box>
        <Button variant="outline" onClick={addLine} isDisabled={!selectedAdditionalLine}>
          Add line
        </Button>
      </Flex>

      <Flex mt={3} gap={3} align="center">
        {/* A charge is free text: a description and an amount, nothing more. */}
        <Button variant="outline" onClick={addChargeLine}>
          Add charge
        </Button>
        <Text fontSize="sm" color="secondaryGray.600">
          Charges are settled on the invoice, not through a repayment.
        </Text>
      </Flex>

      {incompleteCharges.length > 0 && (
        <Text mt={2} fontSize="sm" color="red.500">
          Every charge needs a description and an amount greater than zero.
        </Text>
      )}

      <SimpleGrid columns={2} spacing={4} mt={6} maxW="320px" ml="auto">
        <Text fontWeight={700}>Total due</Text>
        <Text fontWeight={800} textAlign="right">
          <Currency value={totalDue} maximumFractionDigits={2} />
        </Text>
      </SimpleGrid>
    </Box>
  );
};

ObligationLinesTable.propTypes = {
  lines: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  remainingInstallments: PropTypes.array,
  facilityFees: PropTypes.array,
  commitmentLength: PropTypes.number,
};

export default ObligationLinesTable;
